import type { Affordance, DigestItem, DigestModel, TriageCategory } from "@reflecto/client-core";
import {
  Bell01,
  Calendar,
  ChevronRight,
  Mail01,
  Phone,
  Send03,
  ShoppingBag03,
} from "@untitledui/icons";
import { base64ImageDataUrl } from "../../utils/format";
import { Icon } from "../Icon";
import { IconButton } from "../IconButton";
import { affordanceFor } from "./digestModel";
import styles from "./Glance.module.css";

export interface DigestCardLabels {
  needsYouLabel: string;
  quietLabel: string;
  /** Full cold-cache scanning-status line, taking the number of items still
   *  pending (e.g. "Summarizing 6…") — a function so verb-final locales control
   *  the word order around the count. */
  scanningLabel: (pending: number) => string;
  /** Accessible label for a row's jump button. */
  jumpLabel: string;
  /** CTA text per affordance; `none` is "" (no chip). */
  affordance: Record<Affordance, string>;
}

export interface DigestCardProps {
  digest: DigestModel;
  appIconCache: Record<string, string>;
  labels: DigestCardLabels;
  onJumpTo?: (target: { msgId: string } | { conversationKey: string }) => void;
}

function categoryGlyph(category: TriageCategory) {
  switch (category) {
    case "otp":
    case "financial":
      return Bell01;
    case "personal":
    case "work":
      return Send03;
    case "delivery":
      return ShoppingBag03;
    case "calendar":
      return Calendar;
    case "news":
    case "promo":
    case "social":
      return Mail01;
    case "system":
      return Bell01;
    default:
      return Phone;
  }
}

function RowIcon(props: { item: DigestItem; appIconCache: Record<string, string> }) {
  const b64 = props.appIconCache[props.item.appPackage];
  if (b64) return <img className={styles.rowIcon} src={base64ImageDataUrl(b64)} alt="" />;
  return <Icon as={categoryGlyph(props.item.category)} size="sm" className={styles.rowGlyph} />;
}

function jumpTarget(item: DigestItem): { msgId: string } | { conversationKey: string } | null {
  if (item.conversationKey) return { conversationKey: item.conversationKey };
  if (item.msgId) return { msgId: item.msgId };
  return null;
}

export function DigestCard(props: DigestCardProps) {
  const { digest, appIconCache, labels, onJumpTo } = props;
  const quietTotal = digest.quiet.reduce((n, q) => n + q.count, 0);
  // Show progress whenever items remain unclassified — even alongside already-surfaced rows (cold-cache fills in).
  const scanning = digest.pending > 0;
  // digest.overflow (high-stakes items beyond NEEDS_CAP) is intentionally not surfaced yet — the "+N more"
  // line is part of the deferred per-card list UI (spec §9 fast-follow), not an accidental omission.

  return (
    <div className={styles.digest}>
      {digest.lead ? <p className={styles.brief}>{digest.lead}</p> : null}

      {scanning ? (
        <p role="status" className={styles.scanning}>
          {labels.scanningLabel(digest.pending)}
        </p>
      ) : null}

      {digest.needsYou.length > 0 ? (
        <div className={styles.section}>
          <p className={styles.sectionLabel}>{labels.needsYouLabel}</p>
          <ul className={styles.rows}>
            {digest.needsYou.map((item) => {
              const aff: Affordance = affordanceFor(item, { repliable: item.repliable ?? false });
              const target = jumpTarget(item);
              return (
                <li key={item.id} className={styles.row}>
                  <RowIcon item={item} appIconCache={appIconCache} />
                  <div className={styles.rowBody}>
                    <span className={styles.rowLabel}>{item.app}</span>
                    <span className={styles.rowText}>{item.oneLiner}</span>
                  </div>
                  {labels.affordance[aff] ? (
                    <span className={styles.affordance}>{labels.affordance[aff]}</span>
                  ) : null}
                  {onJumpTo && target ? (
                    <IconButton
                      icon={ChevronRight}
                      variant="default"
                      size="s"
                      label={labels.jumpLabel}
                      onClick={() => onJumpTo(target)}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {digest.quiet.length > 0 ? (
        <p className={styles.quiet}>
          {labels.quietLabel} · {quietTotal} ·{" "}
          {digest.quiet.map((q) => `${q.count} ${q.category}`).join(", ")}
        </p>
      ) : null}
    </div>
  );
}
